import { useEffect, useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { AspectRatio, Flex, useDimensions } from "@chakra-ui/react";
import SnapshotMakerBackground from "./SnapshotMakerBackground";
import SnapshotMakerCard from "./SnapshotMakerCard";

// Fix for Suspend `display: "none" !important`: https://github.com/facebook/react/issues/14536#issuecomment-550457864
// Should be fixed in React 18: https://github.com/facebook/react/issues/18141#issuecomment-591648972
const useCallbackOnUnsuspendEffect = (ref, callback) => {
  return useEffect(() => {
    const node = ref.current;

    if (node == null) return;

    const observer = new global.MutationObserver(mutations => {
      if (mutations.find(m => m.attributeName === 'style')) {
        callback();
      }
    });

    observer.observe(node, { attributes: true });

    return () => observer.disconnect();
  }, [callback, ref]);
};

const SnapshotMakerPreview = ({
  data,
  sizeConfig,
  backgroundConfig,
  cardConfig,
  watermark,
}) => {
  const ref = useRef();
  const dimensions = useDimensions(ref, true);
  const [scale, setScale] = useState(1);

  const updateScale = useCallback(() => {
    if (!ref.current) return;
    setScale(ref.current.getBoundingClientRect().width / 640);
  }, [setScale, ref]);

  const setRef = useCallback((node) => {
    if (!node) return;
    ref.current = node;

    updateScale();
    
    // Fix 0 values in some cases eg. when user back from login page
    setTimeout(() => {
      updateScale();
    }, 250);
  }, [updateScale]);

  useEffect(() => {
    if (!dimensions) return;
    updateScale();
  }, [dimensions]);

  useEffect(() => {
    updateScale();
  }, [sizeConfig]);

  useCallbackOnUnsuspendEffect(ref, () => {
    updateScale();
  });

  return (
    <AspectRatio
      ratio={sizeConfig?.width && sizeConfig?.height ? sizeConfig.width / sizeConfig.height : 1}
      fontSize={`${16 * scale}px`}
      background="#ffffff"
      ref={setRef}
    >
      <Flex
        alignItems="center"
        justifyContent="center"
        position="relative"
        zIndex="0"
      >
        <SnapshotMakerBackground
          config={backgroundConfig}
        />
        <SnapshotMakerCard
          config={cardConfig}
          data={data}
          watermark={watermark}
        />
      </Flex>
    </AspectRatio>
  );
};


SnapshotMakerPreview.propTypes = {
  data: PropTypes.shape({
    name: PropTypes.string,
    nickname: PropTypes.string,
    avatarUrl: PropTypes.string,
    message: PropTypes.string,
  }),
  sizeConfig: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  backgroundConfig: PropTypes.shape({}),
  cardConfig: PropTypes.shape({}),
  watermark: PropTypes.bool,
};

SnapshotMakerPreview.defaultProps = {
  data: undefined,
  sizeConfig: undefined,
  backgroundConfig: undefined,
  cardConfig: undefined,
  watermark: undefined,
};

export default SnapshotMakerPreview;
